import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ContactForm from '../../components/base/ContactForm';
import TestimonialsSlider from '../../components/base/TestimonialsSlider';

export default function RoofPaintingPage() {
  useEffect(() => {
    document.title = 'Roof Painting Durbanville | Professional Roof Painters Western Cape';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional roof painting services in Durbanville, Western Cape. Expert roof painters specializing in waterproofing, restoration, and protective roof coatings for all roof types.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'roof painting Durbanville, roof painters Western Cape, roof waterproofing, roof restoration, tile roof painting, metal roof painting');
    }
  }, []);

  return (
    <>
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-black to-gray-800">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/34ececbb548839078ef19ea4603ae5f4.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-6xl md:text-8xl font-bold mb-6" style={{ fontFamily: '"Pacifico", serif' }}>
            Roof <span style={{ color: '#e9f638' }}>PAINTING</span>
          </h1>
          <p className="text-2xl md:text-3xl mb-8 font-light">
            Protect your investment with professional roof painting and waterproofing
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/contact" 
              className="text-black px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
              style={{ backgroundColor: '#e9f638' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d4e532'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#e9f638'}
            >
              Get Free Quote
            </a>
            <a 
              href="#services" 
              className="border-2 border-white hover:bg-white hover:text-black text-white px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Our Services
            </a>
          </div>
        </div>
      </section>

      {/* Roof Painting Services */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Roof Painting Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our specialized roof painters in Durbanville provide comprehensive roof painting and waterproofing solutions to protect your property from the elements.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-building-2-line text-2xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Tile Roof Painting</h3>
              <p className="text-gray-600">
                Professional painting and restoration of concrete and clay tile roofs with specialized roof coatings and sealers.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-hammer-line text-2xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Metal Roof Painting</h3>
              <p className="text-gray-600">
                Specialized coatings for corrugated iron, steel, and aluminum roofs with rust prevention and thermal protection.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-drop-line text-2xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Waterproofing</h3>
              <p className="text-gray-600">
                Advanced waterproofing systems to prevent leaks and water damage with long-lasting protective membranes.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-tools-line text-2xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Roof Restoration</h3>
              <p className="text-gray-600">
                Complete roof restoration including cleaning, repairs, priming, and protective coating application.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-sun-line text-2xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Heat Reflection</h3>
              <p className="text-gray-600">
                Energy-efficient roof coatings that reflect heat and UV rays, reducing cooling costs and extending roof life.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-shield-check-line text-2xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Maintenance Programs</h3>
              <p className="text-gray-600">
                Regular roof maintenance and inspection programs to ensure long-term protection and performance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Roof Painting */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Why Invest in Professional Roof Painting?</h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-check-line text-black font-bold"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Leak Prevention</h3>
                    <p className="text-gray-600">Professional roof painting creates a waterproof barrier that prevents costly water damage and structural issues.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-check-line text-black font-bold"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Extended Roof Life</h3>
                    <p className="text-gray-600">Quality roof coatings can extend your roof's lifespan by 10-15 years, delaying expensive roof replacement.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-check-line text-black font-bold"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Energy Savings</h3>
                    <p className="text-gray-600">Reflective roof coatings reduce heat absorption, lowering cooling costs by up to 20% in summer months.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-check-line text-black font-bold"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Improved Appearance</h3>
                    <p className="text-gray-600">Fresh roof painting dramatically improves your property's curb appeal and overall aesthetic value.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Beautiful%20Durbanville%20home%20with%20freshly%20painted%20roof%2C%20excellent%20condition%2C%20modern%20architecture%2C%20well-maintained%20property%2C%20South%20African%20residential%20area%2C%20professional%20roof%20restoration%20results%2C%20attractive%20curb%20appeal&width=600&height=400&seq=roof-benefits&orientation=landscape"
                alt="Professional roof painting results"
                className="rounded-lg shadow-lg w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Roof Types We Service */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">Roof Types We Service</h2>
            <p className="text-xl text-gray-600">
              Our experienced roof painters work with all types of roofing materials
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-building-2-line text-2xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold mb-2">Concrete Tiles</h3>
              <p className="text-gray-600">Professional painting and sealing of concrete roof tiles with specialized coatings.</p>
            </div>

            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-building-line text-2xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold mb-2">Clay Tiles</h3>
              <p className="text-gray-600">Restoration and protective coating of traditional clay roof tiles.</p>
            </div>

            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-hammer-line text-2xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold mb-2">Metal Roofs</h3>
              <p className="text-gray-600">Specialized coatings for corrugated iron, steel, and aluminum roofing.</p>
            </div>

            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-home-2-line text-2xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold mb-2">Flat Roofs</h3>
              <p className="text-gray-600">Waterproofing and protective coatings for flat and low-slope roofs.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">Our Other Painting Services</h2>
            <p className="text-xl text-gray-600">
              Explore our complete range of professional painting services in Durbanville
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <a href="/interior-painting" className="group bg-white p-6 rounded-lg transition-all duration-300 cursor-pointer" onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9f638'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}>
              <div className="w-12 h-12 group-hover:bg-black rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-home-4-line text-xl text-black group-hover:text-yellow-400"></i>
              </div>
              <h3 className="text-xl font-bold mb-2 group-hover:text-black">Interior Painting</h3>
              <p className="text-gray-600 group-hover:text-black">Professional interior painting for homes and offices.</p>
            </a>

            <a href="/exterior-painting" className="group bg-white p-6 rounded-lg transition-all duration-300 cursor-pointer" onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9f638'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}>
              <div className="w-12 h-12 group-hover:bg-black rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-home-line text-xl text-black group-hover:text-yellow-400"></i>
              </div>
              <h3 className="text-xl font-bold mb-2 group-hover:text-black">Exterior Painting</h3>
              <p className="text-gray-600 group-hover:text-black">Professional exterior painting services for homes and buildings.</p>
            </a>

            <a href="/commercial-painting" className="group bg-white p-6 rounded-lg transition-all duration-300 cursor-pointer" onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9f638'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}>
              <div className="w-12 h-12 group-hover:bg-black rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-building-line text-xl text-black group-hover:text-yellow-400"></i>
              </div>
              <h3 className="text-xl font-bold mb-2 group-hover:text-black">Commercial Painting</h3>
              <p className="text-gray-600 group-hover:text-black">Large-scale commercial and industrial painting projects.</p>
            </a>

            <a href="/residential-painting" className="group bg-white p-6 rounded-lg transition-all duration-300 cursor-pointer" onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9f638'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}>
              <div className="w-12 h-12 group-hover:bg-black rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-home-4-line text-xl text-black group-hover:text-yellow-400"></i>
              </div>
              <h3 className="text-xl font-bold mb-2 group-hover:text-black">Residential Painting</h3>
              <p className="text-gray-600 group-hover:text-black">Complete residential painting solutions for your home.</p>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Get Your Roof Painting Quote</h2>
          <p className="text-xl text-gray-300 mb-12">
            Protect your investment with professional roof painting. Get a free quote from Durbanville's roof specialists.
          </p>
          <ContactForm />
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <TestimonialsSlider />
        </div>
      </section>

      {/* Elfsight AI Chatbot */}
      <script src="https://elfsightcdn.com/platform.js" async></script>
      <div className="elfsight-app-bd0c8f1c-533d-413e-bee7-63e0ccb9fdbf" data-elfsight-app-lazy></div>

      {/* ProveSource Code */}
      <script dangerouslySetInnerHTML={{
        __html: `!function(o,i){window.provesrc&&window.console&&console.error&&console.error("ProveSource is included twice in this page."),provesrc=window.provesrc={dq:[],display:function(){this.dq.push(arguments)}},o._provesrcAsyncInit=function(){provesrc.init({apiKey:"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhY2NvdW50SWQiOiI2NmQzYzc2NjQ3MWQwZDE2NTI2N2VkZDAiLCJpYXQiOjE3MjUxNTUxNzR9.F74G52UprvsGoyXgXsaIMG-PS-c2G7yr84Ut5rrQ4cg",v:"0.0.4"})};var r=i.createElement("script");r.type="text/javascript",r.async=!0,r["ch"+"ar"+"set"]="UTF-8",r.src="https://cdn.provesrc.com/provesrc.js";var e=i.getElementsByTagName("script")[0];e.parentNode.insertBefore(r,e)}(window,document);`
      }} />

      <Footer />
    </>
  );
}
